import { Inject, Injectable, Renderer2 } from '@angular/core'
import { combineLatest, Subject, ReplaySubject } from 'rxjs'
import { filter, map, shareReplay, take, tap } from 'rxjs/operators'
import { AdBlockService } from '@flosportsinc/ng-ad-block'
import { PlatformService } from '../../singleton-services/platform.service'
import { VerticalService } from '../../singleton-services/vertical.service'
import { LocationService } from '../../singleton-services/location.service'
import { IVideoLoggingPostBody, VideoEventLoggingService } from './video-event-logging.service'
import { ExperimentationService } from '../../shared/experimentation/experimentation.service'
import {
  EXP_LIVE_PREROLL_ID,
  EXP_LIVE_PREROLL_VARIATION,
  FEAT_LIVE_ADS_MAYDAY_ID,
  FEAT_LIVE_ADS_MAYDAY_VARIATION
} from '../../shared/experimentation/experiment.model'

import {
  mapGetHead,
  mapInitializedAdContainer,
  mapNewAdsLoader,
  mapVideoAdsLibrary
} from '../functions/utility-functions'
import { notNullOrUndefined } from 'src/app/shared/functions/type-guards'
import { WINDOW } from 'src/app/app.injections'

@Injectable()
export class VideoAdsService {
  constructor(
    @Inject(WINDOW) private readonly window: Window,
    private readonly locationService: LocationService,
    private readonly verticalService: VerticalService,
    private readonly adBlockService: AdBlockService,
    private readonly videoEventLoggingService: VideoEventLoggingService,
    private readonly platformService: PlatformService,
    private readonly es: ExperimentationService
  ) {}

  private readonly adsLoaderSubject = new ReplaySubject<google.ima.AdsLoader>(1)
  private readonly adsManagerSubject = new ReplaySubject<google.ima.AdsManager>(1)
  private readonly init$ = new Subject<Renderer2>()

  private readonly canLoadAds$ = combineLatest([
    this.adBlockService.isAnAdBlockerActive(),
    this.es.isInVariation(FEAT_LIVE_ADS_MAYDAY_ID, FEAT_LIVE_ADS_MAYDAY_VARIATION)
  ]).pipe(
    map(([adBlockerIsActive, emergencyShutOffIsOn]) => {
      return this.platformService.isBrowser && !adBlockerIsActive && !emergencyShutOffIsOn
    })
  )

  public readonly adsLoader$ = this.adsLoaderSubject.asObservable()
  public readonly adsManager$ = this.adsManagerSubject.asObservable()

  public readonly videoAdsLibrary$ = combineLatest([this.init$, this.canLoadAds$]).pipe(
    filter(([_, canLoadAds]) => canLoadAds),
    mapGetHead(),
    mapVideoAdsLibrary(this.window),
    filter(notNullOrUndefined),
    take(1),
    shareReplay(1)
  )

  public init = (renderer2: Renderer2) => this.init$.next(renderer2)

  public getAdsLoader$ = (adDisplayContainer: google.ima.AdDisplayContainer) =>
    this.videoAdsLibrary$.pipe(
      mapNewAdsLoader(adDisplayContainer),
      tap(adsLoader => {
        // Store the adsLoader when created
        if (adsLoader) {
          this.adsLoaderSubject.next(adsLoader)
        }
      })
    )

  public setAdsManager(adsManager: google.ima.AdsManager) {
    this.adsManagerSubject.next(adsManager)
  }

  public videoAdUnit$ = (adDuration: string | number) =>
    this.verticalService.siteName$.pipe(
      map(siteName => {
        const lowerCaseSiteName = siteName?.toLowerCase().replace(/\s+/g, '')
        return `/43625987/live/${lowerCaseSiteName}/${adDuration}`
      })
    )

  public getAdDisplayContainer$ = (contentVideoElement: HTMLVideoElement, adContainerElement: HTMLElement) =>
    this.videoAdsLibrary$.pipe(mapInitializedAdContainer(contentVideoElement, adContainerElement))

  public logAdEvent = (tokenId: string, log: IVideoLoggingPostBody) =>
    this.videoEventLoggingService.logAdEvent(tokenId, log)

  public logAdBlock = (tokenId: string, log: IVideoLoggingPostBody) =>
    this.adBlockService
      .isAnAdBlockerActive()
      .pipe(filter(Boolean), take(1))
      .subscribe(_ => this.videoEventLoggingService.logAdEvent(tokenId, log))

  public readonly canLoadPrerollAds$ = combineLatest([
    this.canLoadAds$,
    this.es.isInVariation(EXP_LIVE_PREROLL_ID, EXP_LIVE_PREROLL_VARIATION)
  ]).pipe(map(([canLoadAds, prerollExperimentOn]) => canLoadAds && prerollExperimentOn))
}
